import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styles from './MercadoPagoAuthModal.module.scss';
import ModalOverlay from '../ModalOverlay/ModalOverlay.tsx';
import { CreditCard, Shield, Users, CheckCircle, ArrowRight } from 'lucide-react';

interface MercadoPagoAuthModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onCancel: () => void;
  projectType: 'proyecto' | 'anteproyecto';
  consultationPrice: number;
  currency: string;
  hasLinkedAccount?: boolean;
}

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1.0,
    y: 0,
    transition: { 
      delay: 0.1,
      duration: 0.3,
      ease: "easeOut"
    },
  },
};

const MercadoPagoAuthModal: React.FC<MercadoPagoAuthModalProps> = ({
  isOpen,
  onAccept,
  onCancel,
  projectType,
  consultationPrice,
  currency,
  hasLinkedAccount = false
}) => {
  const [hasAccepted, setHasAccepted] = useState(hasLinkedAccount);

  if (!isOpen) return null;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency === 'ARS' ? 'ARS' : 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const commissionPercentage = 10; // 10% de comisión
  const commissionAmount = (consultationPrice * commissionPercentage) / 100;
  const userEarnings = consultationPrice - commissionAmount;

  return (
    <ModalOverlay isOpen={isOpen} onClose={onCancel}>
      <motion.div
        className={styles.modal}
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <div className={styles.header}>
          <div className={styles.iconContainer}>
            <CreditCard className={styles.icon} />
          </div>
          <h2 className={styles.title}>
            {hasLinkedAccount ? 'Publicar tu ' + (projectType === 'proyecto' ? 'proyecto' : 'anteproyecto') : 'Vincula tu cuenta de Mercado Pago'}
          </h2>
          <p className={styles.subtitle}>
            {hasLinkedAccount 
              ? 'Confirmá los detalles de pago y publicá tu ' + (projectType === 'proyecto' ? 'proyecto' : 'anteproyecto') + ' en nuestro marketplace'
              : 'Para recibir pagos por tus consultas de ' + (projectType === 'proyecto' ? 'proyecto' : 'anteproyecto')
            }
          </p>
        </div>

        <div className={styles.content}>
          {!hasLinkedAccount && (
            <div className={styles.explanation}>
              <h3 className={styles.sectionTitle}>¿Por qué necesitamos esto?</h3>
              <p className={styles.description}>
                Para que puedas recibir pagos directamente en tu cuenta cuando alguien contrate tus servicios, 
                necesitamos vincular tu cuenta de Mercado Pago de forma segura.
              </p>
            </div>
          )}
          
          {hasLinkedAccount && (
            <div className={styles.explanation}>
              <h3 className={styles.sectionTitle}>Usaremos tu cuenta de Mercado Pago vinculada</h3>
              <p className={styles.description}>
                Ya tenés tu cuenta de Mercado Pago vinculada. Usaremos esos datos para recibir los pagos por tus consultas.
              </p>
            </div>
          )}

          {!hasLinkedAccount && (
            <div className={styles.benefits}>
              <h3 className={styles.sectionTitle}>Beneficios para ti:</h3>
              <div className={styles.benefitsList}>
                <div className={styles.benefitItem}>
                  <CheckCircle className={styles.benefitIcon} />
                  <div>
                    <strong>Pagos automáticos</strong>
                    <p>Recibes el dinero directamente en tu cuenta</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <Shield className={styles.benefitIcon} />
                  <div>
                    <strong>100% seguro</strong>
                    <p>Usamos la tecnología de Mercado Pago</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <Users className={styles.benefitIcon} />
                  <div>
                    <strong>Más clientes</strong>
                    <p>Tu {projectType} será visible en nuestro marketplace</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className={styles.pricing}>
            <h3 className={styles.sectionTitle}>¿Cómo funciona la comisión?</h3>
            <div className={styles.pricingCard}>
              <div className={styles.pricingRow}>
                <span>Precio de tu {projectType === 'proyecto' ? 'Proyecto' : 'Anteproyecto'}:</span>
                <span className={styles.price}>{formatPrice(consultationPrice)}</span>
              </div>
              <div className={styles.pricingRow}>
                <span>Comisión de Edify ({commissionPercentage}%):</span>
                <span className={styles.commission}>-{formatPrice(commissionAmount)}</span>
              </div>
              <div className={styles.pricingRowTotal}>
                <span><strong>Tu ganancia:</strong></span>
                <span className={styles.earnings}><strong>{formatPrice(userEarnings)}</strong></span>
              </div>
            </div>
            <p className={styles.pricingNote}>
              Solo pagas comisión cuando recibes un pago. No hay costos de registro.
            </p>
            {currency === 'USD' && (
              <div className={styles.currencyNote}>
                <strong>Nota:</strong> El cobro se realizará en pesos argentinos al tipo de cambio oficial 
                del Banco Nación al momento del pago.
              </div>
            )}
          </div>

          {!hasLinkedAccount && (
            <div className={styles.security}>
              <h3 className={styles.sectionTitle}>Tu información está segura</h3>
              <div className={styles.securityFeatures}>
                <div className={styles.securityItem}>
                  <Shield className={styles.securityIcon} />
                  <span>Autorización temporal y segura</span>
                </div>
                <div className={styles.securityItem}>
                  <Shield className={styles.securityIcon} />
                  <span>No guardamos datos sensibles</span>
                </div>
                <div className={styles.securityItem}>
                  <Shield className={styles.securityIcon} />
                  <span>Puedes revocar el acceso cuando quieras</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {!hasLinkedAccount && (
            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                id="acceptAuth"
                checked={hasAccepted}
                onChange={(e) => setHasAccepted(e.target.checked)}
                className={styles.checkbox}
              />
              <label htmlFor="acceptAuth" className={styles.checkboxLabel}>
                Entiendo y acepto vincular mi cuenta de Mercado Pago para recibir pagos
              </label>
            </div>
          )}
          
          <div className={styles.buttons}>
            <button
              type="button"
              onClick={onCancel}
              className={styles.cancelButton}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onAccept}
              disabled={!hasAccepted}
              className={styles.acceptButton}
            >
              <span>{hasLinkedAccount ? 'Confirmar y publicar' : 'Continuar con Mercado Pago'}</span>
              <ArrowRight className={styles.buttonIcon} />
            </button>
          </div>
        </div>
      </motion.div>
    </ModalOverlay>
  );
};

export default MercadoPagoAuthModal;
